"use client";

import { cva, type VariantProps } from "class-variance-authority";
import { Check, Eraser, Pencil, RotateCcw } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

// CONTAINER STYLING
const signaturePadContainerVariants = cva(
  "group relative flex w-full flex-col gap-2 transition-all duration-200",
  {
    variants: {
      variant: {
        card: [
          "rounded-lg border bg-card p-4 text-card-foreground shadow-sm",
          "hover:shadow-md",
        ],
        outline: ["rounded-lg border-2 p-4", "hover:border-primary/50"],
        ghost: ["rounded-lg p-4 hover:bg-muted/50"],
        minimal: "p-2",
      },
      size: {
        sm: "gap-1",
        md: "gap-2",
        lg: "gap-3",
      },
    },
    defaultVariants: {
      variant: "minimal",
      size: "md",
    },
  }
);

// CANVAS DEFAULTS
const CANVAS_WIDTH = 400;
const CANVAS_HEIGHT = 200;
const DEFAULT_HOLD_DURATION = 1500; // 1.5 seconds

const disableTouchScroll = (canvas: HTMLCanvasElement): (() => void) => {
  const preventScroll = (e: TouchEvent): void => {
    e.preventDefault();
  };

  canvas.addEventListener("touchstart", preventScroll, { passive: false });
  canvas.addEventListener("touchmove", preventScroll, { passive: false });
  canvas.addEventListener("touchend", preventScroll, { passive: false });

  return (): void => {
    canvas.removeEventListener("touchstart", preventScroll);
    canvas.removeEventListener("touchmove", preventScroll);
    canvas.removeEventListener("touchend", preventScroll);
  };
};

// MAIN PROPS INTERFACE
export interface SignaturePadItemProps
  extends VariantProps<typeof signaturePadContainerVariants> {
  ariaLabel?: string;
  canvasHeight?: number;
  // Canvas options
  canvasWidth?: number;
  className?: string;
  classNames?: {
    container?: string;
    canvas?: string;
    preview?: string;
    dialog?: string;
    actions?: string;
  };
  description?: string;
  disabled?: boolean;
  error?: string;
  holdDuration?: number;
  // Core
  id?: string | number;
  label?: string;
  onChange: (signature: string | null) => void;
  required?: boolean;
  strokeColor?: string;
  strokeWidth?: number;
  title?: string;
  value?: string | null;
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Component handles canvas drawing, touch/mouse events, dialog modals, hold-to-clear gesture, and multiple UI states
export function SignaturePadItem({
  value,
  onChange,
  disabled = false,
  className,
  classNames,
  variant,
  size,
  label,
  description,
  title,
  error,
  required,
  ariaLabel,
  canvasWidth = CANVAS_WIDTH,
  canvasHeight = CANVAS_HEIGHT,
  strokeWidth = 2,
  holdDuration = DEFAULT_HOLD_DURATION,
}: SignaturePadItemProps): React.ReactElement {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [lastPosition, setLastPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [holdProgress, setHoldProgress] = useState(0);
  const [isHolding, setIsHolding] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const holdTimerRef = useRef<NodeJS.Timeout | null>(null);
  const holdStartRef = useRef<number | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  // Helper function to get the correct stroke color based on theme
  const getStrokeColor = useCallback((): string => {
    const isDarkClass = document.documentElement.classList.contains("dark");
    const isLightClass = document.documentElement.classList.contains("light");
    const systemPrefersDark = window.matchMedia(
      "(prefers-color-scheme: dark)"
    ).matches;

    const isDarkMode = isDarkClass || (!isLightClass && systemPrefersDark);
    return isDarkMode ? "#ffffff" : "#000000";
  }, []);

  // Setup canvas when dialog opens
  useEffect(() => {
    if (!isDialogOpen) {
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.lineWidth = strokeWidth;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = getStrokeColor();
    }

    const updateStrokeColor = (): void => {
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        return;
      }
      ctx.strokeStyle = getStrokeColor();
    };

    const cleanupTouchScroll = disableTouchScroll(canvas);

    const observer = new MutationObserver(updateStrokeColor);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    mediaQuery.addEventListener("change", updateStrokeColor);

    return (): void => {
      cleanupTouchScroll();
      observer.disconnect();
      mediaQuery.removeEventListener("change", updateStrokeColor);
    };
  }, [isDialogOpen, strokeWidth, getStrokeColor]);

  // Reset canvas state when dialog opens
  useEffect(() => {
    if (isDialogOpen) {
      setHasDrawn(false);
      setHoldProgress(0);
      setIsHolding(false);
      // Clear canvas on open
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d");
      if (canvas && ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    }
  }, [isDialogOpen]);

  const startDrawing = (
    e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
  ): void => {
    e.preventDefault();
    setIsDrawing(true);
    setHasDrawn(true);
    draw(e);
  };

  const stopDrawing = (): void => {
    if (!isDrawing) {
      return;
    }
    setIsDrawing(false);
    setLastPosition(null);
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (canvas && ctx) {
      ctx.beginPath();
    }
  };

  function draw(
    e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
  ): void {
    e.preventDefault();
    if (!isDrawing) {
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (canvas && ctx) {
      // Ensure correct stroke color before drawing
      ctx.strokeStyle = getStrokeColor();

      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const x =
        (("touches" in e ? (e.touches[0]?.clientX ?? 0) : e.clientX) -
          rect.left) *
        scaleX;
      const y =
        (("touches" in e ? (e.touches[0]?.clientY ?? 0) : e.clientY) -
          rect.top) *
        scaleY;

      if (lastPosition) {
        const midX = (lastPosition.x + x) / 2;
        const midY = (lastPosition.y + y) / 2;

        ctx.beginPath();
        ctx.moveTo(lastPosition.x, lastPosition.y);
        ctx.quadraticCurveTo(midX, midY, x, y);
        ctx.stroke();
      } else {
        ctx.beginPath();
        ctx.moveTo(x, y);
      }

      setLastPosition({ x, y });
    }
  }

  const clearSignature = (): void => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (canvas && ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setHasDrawn(false);
      setHoldProgress(0);
    }
  };

  const updateHoldProgress = useCallback((): void => {
    if (!holdStartRef.current) {
      return;
    }

    const elapsed = Date.now() - holdStartRef.current;
    const progress = Math.min((elapsed / holdDuration) * 100, 100);
    setHoldProgress(progress);

    if (progress < 100) {
      animationFrameRef.current = requestAnimationFrame(updateHoldProgress);
    }
  }, [holdDuration]);

  const handleHoldStart = useCallback(
    (e: React.PointerEvent): void => {
      if (!hasDrawn) {
        return;
      }

      e.preventDefault();
      setIsHolding(true);
      holdStartRef.current = Date.now();
      setHoldProgress(0);

      // Start progress animation
      animationFrameRef.current = requestAnimationFrame(updateHoldProgress);

      // Set timer for completion
      holdTimerRef.current = setTimeout(() => {
        const canvas = canvasRef.current;
        if (canvas) {
          const dataUrl = canvas.toDataURL("image/png");
          onChange(dataUrl);
          setIsDialogOpen(false);
        }
        setIsHolding(false);
        setHoldProgress(0);
      }, holdDuration);
    },
    [hasDrawn, holdDuration, onChange, updateHoldProgress]
  );

  const handleHoldEnd = useCallback((): void => {
    setIsHolding(false);
    holdStartRef.current = null;

    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    setHoldProgress(0);
  }, []);

  const handleClearValue = (): void => {
    onChange(null);
  };

  const handleReset = (): void => {
    onChange(null);
    setIsDialogOpen(false);
  };

  const handleEditSignature = (): void => {
    setIsDialogOpen(true);
  };

  // Cleanup timers on unmount
  useEffect(() => {
    return (): void => {
      if (holdTimerRef.current) {
        clearTimeout(holdTimerRef.current);
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  // If we have a signature value, show the signature image
  if (value) {
    return (
      <div
        className={cn(
          signaturePadContainerVariants({ variant, size }),
          className,
          classNames?.container
        )}
        data-slot="signature-pad-container"
      >
        {(label || title || description) && (
          <div className="flex flex-col gap-1">
            {(label || title) && (
              // biome-ignore lint/a11y/noLabelWithoutControl: Decorative label for signature pad component
              <label className="font-medium text-sm leading-none">
                {label || title}
                {required && <span className="ml-1 text-destructive">*</span>}
              </label>
            )}
            {description && (
              <p className="text-muted-foreground text-sm">{description}</p>
            )}
          </div>
        )}

        <div
          className={cn(
            "relative inline-flex items-center justify-start",
            classNames?.preview
          )}
          data-slot="signature-preview"
        >
          <div className="relative overflow-hidden rounded-md border border-input bg-white dark:bg-zinc-900">
            {/* biome-ignore lint/performance/noImgElement: Data URL from canvas, not optimizable by Next.js Image */}
            {/* biome-ignore lint/correctness/useImageSize: Dynamic data URL, dimensions set via className */}
            <img
              alt={ariaLabel || "Signature"}
              className="h-[100px] w-[200px] object-contain"
              src={value}
            />
            <div
              className={cn(
                "absolute right-1 bottom-1 flex gap-1",
                classNames?.actions
              )}
              data-slot="signature-actions"
            >
              <Button
                aria-label="Edit signature"
                className="h-7 w-7 rounded-full bg-background/80 backdrop-blur-sm"
                disabled={disabled}
                onClick={handleEditSignature}
                size="icon"
                type="button"
                variant="outline"
              >
                <Pencil className="h-3 w-3 text-muted-foreground hover:text-primary" />
              </Button>
              <Button
                aria-label="Clear signature"
                className="h-7 w-7 rounded-full bg-background/80 backdrop-blur-sm"
                disabled={disabled}
                onClick={handleClearValue}
                size="icon"
                type="button"
                variant="outline"
              >
                <Eraser className="h-3 w-3 text-muted-foreground hover:text-primary" />
              </Button>
            </div>
          </div>

          <Dialog onOpenChange={setIsDialogOpen} open={isDialogOpen}>
            <DialogContent
              className={cn("sm:max-w-md", classNames?.dialog)}
              data-slot="signature-dialog"
            >
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Pencil className="h-4 w-4" />
                  Draw Signature
                </DialogTitle>
                <DialogDescription>
                  Draw your signature below, then hold the confirm button to
                  sign.
                </DialogDescription>
              </DialogHeader>

              <div
                className={cn(
                  "relative overflow-hidden rounded-xl border border-input bg-background",
                  classNames?.canvas
                )}
                data-slot="signature-canvas"
              >
                <canvas
                  aria-label={ariaLabel || "Signature drawing canvas"}
                  className="h-[200px] w-full cursor-crosshair touch-none"
                  height={canvasHeight}
                  onBlur={stopDrawing}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseOut={stopDrawing}
                  onMouseUp={stopDrawing}
                  onTouchEnd={stopDrawing}
                  onTouchMove={draw}
                  onTouchStart={startDrawing}
                  ref={canvasRef}
                  width={canvasWidth}
                />
                <Button
                  aria-label="Clear canvas"
                  className="absolute bottom-2 left-2 h-8 w-8 rounded-full"
                  onClick={clearSignature}
                  size="icon"
                  type="button"
                  variant="outline"
                >
                  <Eraser className="h-4 w-4 text-muted-foreground" />
                </Button>
              </div>

              <DialogFooter className="flex-row gap-2 sm:flex-row">
                <Button
                  className="flex-shrink-0"
                  onClick={handleReset}
                  type="button"
                  variant="destructive"
                >
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Reset
                </Button>
                <Button
                  aria-label="Hold to confirm signature"
                  className={cn(
                    "relative flex-1 overflow-hidden border-2 border-transparent transition-all",
                    hasDrawn
                      ? "bg-zinc-800 text-white hover:bg-zinc-700 dark:bg-zinc-200 dark:text-zinc-900 dark:hover:bg-zinc-300"
                      : "cursor-not-allowed bg-muted text-muted-foreground"
                  )}
                  disabled={!hasDrawn}
                  onPointerCancel={handleHoldEnd}
                  onPointerDown={handleHoldStart}
                  onPointerLeave={handleHoldEnd}
                  onPointerUp={handleHoldEnd}
                  type="button"
                >
                  {/* Progress fill background */}
                  <span
                    className="absolute top-0 bottom-0 left-0 bg-emerald-500 transition-none"
                    style={{ width: `${holdProgress}%` }}
                  />
                  <span className="relative z-10 flex items-center gap-2">
                    <Check
                      className={cn("h-4 w-4", isHolding && "animate-pulse")}
                    />
                    {isHolding ? "Keep holding..." : "Hold to confirm"}
                  </span>
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {error && (
          <p className="text-destructive text-sm" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }

  // Initial state: show pen button
  return (
    <div
      className={cn(
        signaturePadContainerVariants({ variant, size }),
        className,
        classNames?.container
      )}
      data-slot="signature-pad-container"
    >
      {(label || title || description) && (
        <div className="flex flex-col gap-1">
          {(label || title) && (
            // biome-ignore lint/a11y/noLabelWithoutControl: Decorative label for signature pad component
            <label className="font-medium text-sm leading-none">
              {label || title}
              {required && <span className="ml-1 text-destructive">*</span>}
            </label>
          )}
          {description && (
            <p className="text-muted-foreground text-sm">{description}</p>
          )}
        </div>
      )}

      <div className="inline-flex">
        <Button
          aria-label={ariaLabel || "Open signature pad"}
          className="rounded-full"
          disabled={disabled}
          onClick={(): void => setIsDialogOpen(true)}
          size="icon"
          type="button"
          variant="outline"
        >
          <Pencil className="h-4 w-4" />
        </Button>
      </div>

      <Dialog onOpenChange={setIsDialogOpen} open={isDialogOpen}>
        <DialogContent
          className={cn("sm:max-w-md", classNames?.dialog)}
          data-slot="signature-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-4 w-4" />
              Draw Signature
            </DialogTitle>
            <DialogDescription>
              Draw your signature below, then hold the confirm button to sign.
            </DialogDescription>
          </DialogHeader>

          <div
            className={cn(
              "relative overflow-hidden rounded-xl border border-input bg-background",
              classNames?.canvas
            )}
            data-slot="signature-canvas"
          >
            <canvas
              aria-label={ariaLabel || "Signature drawing canvas"}
              className="h-[200px] w-full cursor-crosshair touch-none"
              height={canvasHeight}
              onBlur={stopDrawing}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseOut={stopDrawing}
              onMouseUp={stopDrawing}
              onTouchEnd={stopDrawing}
              onTouchMove={draw}
              onTouchStart={startDrawing}
              ref={canvasRef}
              width={canvasWidth}
            />
            <Button
              aria-label="Clear canvas"
              className="absolute bottom-2 left-2 h-8 w-8 rounded-full"
              onClick={clearSignature}
              size="icon"
              type="button"
              variant="outline"
            >
              <Eraser className="h-4 w-4 text-muted-foreground" />
            </Button>
          </div>

          <DialogFooter className="flex-row gap-2 sm:flex-row">
            <Button
              aria-label="Hold to confirm signature"
              className={cn(
                "relative flex-1 overflow-hidden border-2 border-transparent transition-all",
                hasDrawn
                  ? "bg-zinc-800 text-white hover:bg-zinc-700 dark:bg-zinc-200 dark:text-zinc-900 dark:hover:bg-zinc-300"
                  : "cursor-not-allowed bg-muted text-muted-foreground"
              )}
              disabled={!hasDrawn}
              onPointerCancel={handleHoldEnd}
              onPointerDown={handleHoldStart}
              onPointerLeave={handleHoldEnd}
              onPointerUp={handleHoldEnd}
              type="button"
            >
              {/* Progress fill background */}
              <span
                className="absolute top-0 bottom-0 left-0 bg-emerald-500 transition-none"
                style={{ width: `${holdProgress}%` }}
              />
              <span className="relative z-10 flex items-center gap-2">
                <Check
                  className={cn("h-4 w-4", isHolding && "animate-pulse")}
                />
                {isHolding ? "Keep holding..." : "Hold to confirm"}
              </span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {error && (
        <p className="text-destructive text-sm" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

// COLLECTION COMPONENT
export interface SignaturePadCollectionProps {
  className?: string;
  columns?: 1 | 2 | 3 | 4;
  gap?: "sm" | "md" | "lg";
  items: SignaturePadItemProps[];
  layout?: "stack" | "grid" | "inline";
}

export function SignaturePadCollection({
  items,
  layout = "stack",
  columns = 2,
  gap = "md",
  className,
}: SignaturePadCollectionProps): React.ReactElement {
  const containerClasses = cn(
    "w-full",
    {
      "flex flex-col": layout === "stack",
      grid: layout === "grid",
      "flex flex-row flex-wrap": layout === "inline",
    },
    {
      "gap-2": gap === "sm",
      "gap-4": gap === "md",
      "gap-6": gap === "lg",
    },
    layout === "grid" && {
      "grid-cols-1": columns === 1,
      "grid-cols-2": columns === 2,
      "grid-cols-3": columns === 3,
      "grid-cols-4": columns === 4,
    },
    className
  );

  return (
    <div className={containerClasses} data-slot="signature-pad-collection">
      {items.map((item, index) => (
        <SignaturePadItem key={item.id ?? index} {...item} />
      ))}
    </div>
  );
}
